import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

import React, { useRef, useEffect, useState, useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import uuid from 'utils/uuid';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Dropbox from '@uppy/dropbox';
import Instagram from '@uppy/instagram';
import Facebook from '@uppy/facebook';
import AwsS3 from '@uppy/aws-s3';
import useUserContext from 'ecommerce/context/user';

import AncestryProvider from '../lib/uppyAncestryProvider';
import FamilySearchProvider from '../lib/uppyFamilySearchProvider';
import QrProvider from '../lib/uppyQrProvider';

import SETTINGS from '../settings';
import imageApi from '../api/image';

const UploaderContext = createContext();

export const UploaderContextProvider = props => {
  const [isLoading, setIsLoading] = useState(true);
  const userContext = useUserContext();

  // TODO useRef for uppy instance
  const uploaderRef = useRef({
    choosePhotos: () => console.log('Unimplemented method!'),
  });

  useEffect(() => {
    const uppy = new Uppy({
      autoProceed: true,
      allowMultipleUploads: false,
    });

    uppy.use(AwsS3, {
      limit: 2,
      companionUrl: SETTINGS.UPPY_HOST,
    });

    uppy.use(Dashboard, {
      inline: false,
      target: '#DashboardContainer',
      trigger: '#DashboardTrigger', // Trigger, due to nonJQuery design
      replaceTargetContent: true,
      showProgressDetails: true,
      browserBackButtonClose: false,
      proudlyDisplayPoweredByUppy: false,
      hidePauseResumeButton: true,
      showLinkToFileUploadResult: false,
    });

    if (SETTINGS.UPPY_SOURCES.indexOf('dropbox') >= 0) {
      uppy.use(Dropbox, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
      });
    }

    if (SETTINGS.UPPY_SOURCES.indexOf('instagram') >= 0) {
      uppy.use(Instagram, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
      });
    }

    if (SETTINGS.UPPY_SOURCES.indexOf('facebook') >= 0) {
      uppy.use(Facebook, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
      });
    }

    if (SETTINGS.UPPY_SOURCES.indexOf('qr') >= 0) {
      uppy.use(QrProvider, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
        paraboAccessToken: userContext.accessToken,
        onDone: () => uppy.getPlugin('Dashboard').closeModal(),
        onUpload: urls => console.log(urls),
        maxNumberOfFiles: 10,
      });
    }

    if (SETTINGS.UPPY_SOURCES.indexOf('ancestry') >= 0) {
      uppy.use(AncestryProvider, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
        paraboAccessToken: userContext.accessToken,
        ancestryAuth: userContext.ancestryAuth,
      });
    }

    if (SETTINGS.UPPY_SOURCES.indexOf('familysearch') >= 0) {
      uppy.use(FamilySearchProvider, {
        target: Dashboard,
        companionUrl: SETTINGS.UPPY_HOST,
        paraboAccessToken: userContext.accessToken,
        ancestryAuth: userContext.ancestryAuth,
      });
    }

    uploaderRef.current.choosePhotos = (maxNumberOfFiles, callback = undefined) => {
      uppy.cancelAll();

      uppy.setOptions({
        restrictions: {
          allowedFileTypes: ['image/jpg', 'image/jpeg', 'image/png', '.heic', '.heif'],
          minNumberOfFiles: 1,
          maxNumberOfFiles,
        },
      });
      if (callback) {
        const uploadSuccessCallback = (file, response) => {
          const url = decodeURIComponent(response.body.location);
          const type = file.type;

          if (['image/heic', 'image/heif'].includes(type)) {
            (async heicUrl => {
              const { data } = await imageApi(userContext.accessToken).normalizeImage(heicUrl);
              if (data) {
                callback({
                  key: uuid(),
                  url: data.url,
                  tags: [],
                  type: 'image/jpeg',
                });
              }
            })(url);
          } else {
            callback({
              key: uuid(),
              url,
              tags: [],
              type,
            });
          }
        };
        uppy.on('upload-success', uploadSuccessCallback);
        uppy.getPlugin("qr").setOptions({
          maxNumberOfFiles,
          onUpload: (photos = []) => {
            if (photos.length > 0) {
              photos.forEach(photo => {
                callback({
                  key: uuid(),
                  url: photo.url,
                  tags: [],
                  type: photo.type
                })
              })
            }
          }
        })

        uppy.on('complete', () => {
          uppy.off('upload-success', uploadSuccessCallback);
          uppy.getPlugin('Dashboard').closeModal();
        });

        uppy.on('dashboard:modal-closed', () => {
          uppy.off('upload-success', uploadSuccessCallback);
        });
      }
      uppy.getPlugin('Dashboard').openModal();
    };

    setIsLoading(false);
  }, []);

  return (
    <UploaderContext.Provider
      value={{
        isLoading,
        choosePhotos: uploaderRef.current.choosePhotos,
      }}
    >
      {props.children}
      <div id="DashboardContainer" />
      <div id="DashboardTrigger" className="d-none" />
    </UploaderContext.Provider>
  );
};

UploaderContextProvider.propTypes = {
  children: PropTypes.node,
};

const useUploaderContext = () => {
  return useContext(UploaderContext);
};

export default useUploaderContext;
